import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { useTasksTab } from "../../../hooks/Tasks/useTasksTab";
import { TargetStatus, TargetType } from "../../../components/Tasks/types";
import { tokens } from "../../../theme/tokens";
import {
  STATUS_CONFIG,
  TYPE_CONFIG,
  PRIMARY_COLOR,
} from "../../../components/Tasks/constants";
import {
  ObjectiveRow,
  ViewMode,
  ListMode,
  BoardMode,
  GroupedMode,
  getObjectiveDisplayText,
} from "../../../components/Tasks/ObjectivesModes";
import {
  FilterPanel,
  FilterPill,
} from "../../../components/Tasks/ObjectivesFilterPanel";

interface ObjectivesViewProps {
  onJumpToTask: (taskId: string) => void;
}

const ObjectivesView: React.FC<ObjectivesViewProps> = ({ onJumpToTask }) => {
  const { filteredTasks: tasks } = useTasksTab();

  const [mode, setMode] = useState<ViewMode>("list");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<TargetStatus | "">("");
  const [filterType, setFilterType] = useState<TargetType | "">("");
  const [filterTaskId, setFilterTaskId] = useState("");
  const [filterPoiId, setFilterPoiId] = useState("");
  const [search, setSearch] = useState("");

  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);

  const allRows = useMemo<ObjectiveRow[]>(
    () =>
      tasks.flatMap((t) =>
        t.targets.map((target) => ({
          target,
          taskId: t.task_id,
          taskTitle: t.title,
          poiName: t.poi.name,
          poiId: t.poi.id,
          isPrimary: t.primary_target_ids.includes(target.target_id),
        })),
      ),
    [tasks],
  );

  const taskOptions = useMemo(
    () => tasks.map((t) => ({ taskId: t.task_id, title: t.title })),
    [tasks],
  );

  const poiOptions = useMemo(() => {
    const seen = new Map<string, string>();
    allRows.forEach((r) => {
      if (!seen.has(r.poiId)) seen.set(r.poiId, r.poiName);
    });
    return Array.from(seen.entries()).map(([poiId, name]) => ({ poiId, name }));
  }, [allRows]);

  const filteredRows = useMemo(
    () =>
      allRows.filter((row) => {
        if (filterStatus && row.target.status !== filterStatus) return false;
        if (filterType && row.target.type !== filterType) return false;
        if (filterTaskId && row.taskId !== filterTaskId) return false;
        if (filterPoiId && row.poiId !== filterPoiId) return false;
        if (search) {
          const text = getObjectiveDisplayText(row.target).toLowerCase();
          if (!text.includes(search.toLowerCase())) return false;
        }
        return true;
      }),
    [allRows, filterStatus, filterType, filterTaskId, filterPoiId, search],
  );

  // Clear stale task filter when the task is removed
  useEffect(() => {
    if (filterTaskId && !taskOptions.some((o) => o.taskId === filterTaskId)) {
      setFilterTaskId("");
    }
  }, [filterTaskId, taskOptions]);

  // Close on outside click
  useEffect(() => {
    if (!filtersOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        filterPanelRef.current?.contains(e.target as Node) ||
        filterButtonRef.current?.contains(e.target as Node)
      )
        return;
      setFiltersOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filtersOpen]);

  // Close on Escape
  useEffect(() => {
    if (!filtersOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFiltersOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [filtersOpen]);

  const activeFilterCount = [
    filterStatus,
    filterType,
    filterTaskId,
    filterPoiId,
  ].filter(Boolean).length;

  const anyFilterActive = activeFilterCount > 0 || !!search;

  const modeButtons: {
    id: ViewMode;
    Icon: React.ElementType;
    title: string;
  }[] = [
    { id: "list", Icon: FormatListBulletedIcon, title: "List" },
    { id: "board", Icon: ViewColumnIcon, title: "Board" },
    { id: "grouped", Icon: AccountTreeIcon, title: "Grouped" },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        backgroundColor: tokens.color.surface.default,
        border: `1px solid ${tokens.color.border.default}`,
        borderRadius: "10px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: "16px",
          py: "10px",
          borderBottom: `1px solid ${tokens.color.border.default}`,
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexShrink: 0,
        }}
      >
        {/* Title — far left */}
        <span
          style={{
            fontSize: "10px",
            fontWeight: 700,
            color: tokens.color.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          All Objectives
        </span>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Count */}
        <span
          style={{
            fontSize: "11px",
            color: tokens.color.text.secondary,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {filteredRows.length} of {allRows.length}
        </span>

        {/* Filters toggle */}
        <button
          ref={filterButtonRef}
          type="button"
          onClick={() => setFiltersOpen((o) => !o)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "11px",
            fontWeight: 500,
            color: filtersOpen || anyFilterActive ? PRIMARY_COLOR : tokens.color.text.secondary,
            backgroundColor: "transparent",
            border: `1px solid ${filtersOpen || anyFilterActive ? PRIMARY_COLOR : tokens.color.border.default}`,
            borderRadius: "6px",
            padding: "3px 10px",
            cursor: "pointer",
            height: "26px",
            flexShrink: 0,
          }}
        >
          <TuneOutlinedIcon style={{ fontSize: "13px" }} />
          Filters
          {activeFilterCount > 0 && (
            <Box
              component="span"
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                color: tokens.color.surface.default,
                backgroundColor: PRIMARY_COLOR,
                borderRadius: "10px",
                px: "5px",
                lineHeight: "16px",
                ml: "1px",
              }}
            >
              {activeFilterCount}
            </Box>
          )}
        </button>

        {/* Search */}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          style={{
            fontSize: "11px",
            height: "26px",
            padding: "0 8px",
            border: `1px solid ${search ? PRIMARY_COLOR : tokens.color.border.default}`,
            borderRadius: "6px",
            outline: "none",
            color: tokens.color.text.primary,
            width: "120px",
            flexShrink: 0,
          }}
        />

        {/* Mode toggle */}
        <Box
          sx={{
            display: "flex",
            border: `1px solid ${tokens.color.border.default}`,
            borderRadius: "6px",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {modeButtons.map(({ id, Icon, title }, i) => (
            <button
              key={id}
              type="button"
              title={title}
              onClick={() => setMode(id)}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "4px 8px",
                border: "none",
                borderRight: i < 2 ? `1px solid ${tokens.color.border.default}` : "none",
                backgroundColor: mode === id ? PRIMARY_COLOR : tokens.color.surface.default,
                color: mode === id ? tokens.color.surface.default : `${tokens.color.text.secondary}`,
                cursor: "pointer",
                transition: "background-color 0.12s ease, color 0.12s ease",
              }}
            >
              <Icon style={{ fontSize: "15px" }} />
            </button>
          ))}
        </Box>
      </Box>

      {/* Active filter pills row — shown below header whenever filters are active */}
      {activeFilterCount > 0 && (
        <Box
          sx={{
            px: "16px",
            py: "10px",
            borderBottom: `1px solid ${tokens.color.border.default}`,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          {filterPoiId && (
            <FilterPill
              label={`POI: ${poiOptions.find((o) => o.poiId === filterPoiId)?.name ?? "POI"}`}
              onRemove={() => setFilterPoiId("")}
            />
          )}
          {filterStatus && (
            <FilterPill
              label={`Status: ${STATUS_CONFIG[filterStatus].label}`}
              onRemove={() => setFilterStatus("")}
            />
          )}
          {filterType && (
            <FilterPill
              label={`Type: ${TYPE_CONFIG[filterType].label}`}
              onRemove={() => setFilterType("")}
            />
          )}
          {filterTaskId && (
            <FilterPill
              label={`Task: ${taskOptions.find((o) => o.taskId === filterTaskId)?.title ?? "Task"}`}
              onRemove={() => setFilterTaskId("")}
            />
          )}
        </Box>
      )}

      {/* Content area — minHeight ensures filter dropdown is never clipped */}
      <Box
        sx={{
          flex: 1,
          minHeight: "240px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {filtersOpen && (
          <FilterPanel
            filterStatus={filterStatus}
            filterType={filterType}
            filterTaskId={filterTaskId}
            filterPoiId={filterPoiId}
            search={search}
            setFilterStatus={setFilterStatus}
            setFilterType={setFilterType}
            setFilterTaskId={setFilterTaskId}
            setFilterPoiId={setFilterPoiId}
            taskOptions={taskOptions}
            poiOptions={poiOptions}
            allRows={allRows}
            panelRef={filterPanelRef}
            onDone={() => setFiltersOpen(false)}
          />
        )}

        {allRows.length === 0 ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ fontSize: "12px", color: tokens.color.border.strong }}>
              No objectives yet. Add objectives to tasks to see them here.
            </span>
          </Box>
        ) : mode === "list" ? (
          <ListMode rows={filteredRows} onJumpToTask={onJumpToTask} />
        ) : mode === "board" ? (
          <BoardMode rows={filteredRows} onJumpToTask={onJumpToTask} />
        ) : (
          <GroupedMode
            rows={filteredRows}
            tasks={tasks}
            onJumpToTask={onJumpToTask}
          />
        )}
      </Box>
    </Box>
  );
};

export default ObjectivesView;
